import { useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { BiContextProvidedValue, BiData } from '@wix/da-bi/pkg/events';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { publishComment } from '../redux/actionCreators';
import { useVerification } from '../../VerificationPopup/withVerification';
import { CommentEditorContext, EditorContext } from '../context';
import { useEditorDraftActions } from './useEditorDraftActions';
import { CommentEditorSource } from '../redux/types';
/**
 * Provides a function that will post a comment!
 *
 * Relies on all the contexts provided by `<CommentEditorProviders />`,
 * so be sure you have it set up first!
 */ export function useCommentPostAction({ messageId , folderId , shouldDeleteMessage , onPost  } = {}) {
    const { isBusy , editorId , commentableType , commentableItemId , editorSource , relevantComment  } = useContext(CommentEditorContext);
    const { api: editorApi , state: editorState  } = useContext(EditorContext);
    const dispatch = useDispatch();
    const { pushVerificationPopup , isVerificationNeeded  } = useVerification();
    const { removeEditorDraft  } = useEditorDraftActions(editorId);
    const logBiEvent = useBiLogger();
    const postComment = useCallback(async ()=>{
        if (isBusy) {
            return;
        }
        if (isVerificationNeeded) {
            pushVerificationPopup('comment');
            return;
        }
        // Clean up draft state in local storage
        removeEditorDraft();
        if (editorSource !== CommentEditorSource.Edit) {
            logBiEvent(BiData({
                evid: 184,
                itemid: typeof commentableItemId === 'string' ? parseInt(commentableItemId) : commentableItemId,
                typeid: commentableType,
                notification_type: BiContextProvidedValue,
                is_comment_thread: BiContextProvidedValue,
                is_reply: editorSource === CommentEditorSource.Reply ? 1 : 0,
                is_sticky: 0
            }));
        }
        // Handle android, the usual wait wasn't working because it updates the context
        // and for various reasons the state is out of date inside this context. To work
        // around we resolve the state we need from inside the editor.
        const state = await (editorApi === null || editorApi === void 0 ? void 0 : editorApi.resolveEditorStateForAndroid());
        if (state === null || state === void 0 ? void 0 : state.rawContentState) {
            dispatch(publishComment({
                editorId,
                typeId: commentableType,
                itemId: commentableItemId,
                rawContentState: state.rawContentState,
                editorContent: state.editorContent,
                featuresData: state.features,
                editComment: editorSource === CommentEditorSource.Edit ? relevantComment : undefined,
                replyComment: editorSource === CommentEditorSource.Reply ? relevantComment : undefined,
                messageId,
                folderId,
                shouldDeleteMessage
            }));
        } else {
            // Non Android codepath
            dispatch(publishComment({
                editorId,
                typeId: commentableType,
                itemId: commentableItemId,
                rawContentState: editorState.rawContentState,
                editorContent: editorState.editorContent,
                featuresData: editorState.features,
                editComment: editorSource === CommentEditorSource.Edit ? relevantComment : undefined,
                replyComment: editorSource === CommentEditorSource.Reply ? relevantComment : undefined,
                messageId,
                folderId,
                shouldDeleteMessage
            }));
        }
        onPost && onPost();
    }, [
        dispatch,
        isBusy,
        editorId,
        editorSource,
        relevantComment,
        commentableType,
        commentableItemId,
        pushVerificationPopup,
        isVerificationNeeded,
        editorApi,
        editorState,
        removeEditorDraft,
        logBiEvent,
        messageId,
        shouldDeleteMessage,
        folderId,
        onPost
    ]);
    return postComment;
}
